import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  Table,
  Typography,
  Row,
  Col,
  message,
  Tag,
  Modal,
  List,
  Checkbox,
  Slider,
  Tabs,
  Statistic,
  Descriptions,
  Badge,
  Switch,
  Image,
  DatePicker,
  Popconfirm,
} from 'antd';
import {
  RocketOutlined,
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  RobotOutlined,
  LoadingOutlined,
  HistoryOutlined,
  EyeOutlined,
  HolderOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  AuditOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { apiPost, apiGet } from '../services/api';
import { getEmployeeList, BotEmployee } from '../services/botEmployeeApi';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 商务风格色彩
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

interface TalkInfo {
  talkId: number;
  content: string;
  authorId: number;
  authorName: string;
  authorAvatar: string;
  currentLikeCount: number;
  currentCommentCount: number;
  suggestedLikeCount: number;
  suggestedCommentCount: number;
  warningTooManyLikes: boolean;
  hasActiveTask: boolean;
  activeTaskId: string | null;
  viewCount: number;
  timestamp: string;
}

interface CommentItem {
  content: string;
  parentId: number | null;
}

interface TaskItem {
  talkId: number;
  content: string;
  authorName: string;
  currentLikeCount: number;
  targetLikeCount: number;
  comments: CommentItem[];
}

// 任务信息
interface TaskStatus {
  taskId: string;
  talkId: number;
  talkContent: string;
  authorName: string;
  authorUserId: number;
  status: string;
  targetLikeCount: number;
  targetCommentCount: number;
  targetViewCount: number;
  completedLikeCount: number;
  completedCommentCount: number;
  completedViewCount: number;
  failedLikeCount: number;
  failedCommentCount: number;
  failedViewCount: number;
  progress: number;
  createTime: string;
  talkSnapshot?: string;
}

// 步骤信息
interface TaskStep {
  id: number;
  taskId: string;
  stepType: string;
  scheduledTime: string;
  executedTime: string | null;
  botId: number;
  botName: string;
  viewCount: number | null;
  commentContent: string | null;
  status: string;
  errorMessage: string | null;
}

// 自动增长状态
interface AutoBoostStatus {
  enabled: boolean;
  running: boolean;
  aiType: string;
  commentLanguage: string;
  lastRunTime: string | null;
  lastProcessedCount: number;
  lastSubmittedCount: number;
  lastSkippedCount: number;
  lastFailedCount: number;
}

// PageHeader 组件
const PageHeader: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <div style={{ marginBottom: 24 }}>
    <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ color: COLORS.primary }}>{icon}</span>
      {title}
    </Title>
    <div style={{
      width: 60,
      height: 4,
      background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
      borderRadius: 2,
      marginTop: 8,
    }} />
  </div>
);

// SimpleCard 组件
const SimpleCard: React.FC<{ children: React.ReactNode; title?: string; extra?: React.ReactNode; style?: React.CSSProperties }> = ({
  children, title, extra, style
}) => (
  <Card
    title={title}
    extra={extra}
    style={{
      borderRadius: 8,
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      ...style,
    }}
    styles={{
      header: { borderBottom: `1px solid ${COLORS.borderDark}` },
    }}
  >
    {children}
  </Card>
);

// 可拖拽评论项组件
const SortableEditingCommentItem: React.FC<{
  id: string;
  item: CommentItem;
  index: number;
  onDelete: () => void;
}> = ({ id, item, index, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <List.Item
      ref={setNodeRef}
      style={style}
      {...attributes}
      actions={[
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} key="delete" />,
      ]}
    >
      <Space>
        <div {...listeners} style={{ cursor: 'grab' }}>
          <HolderOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />
        </div>
        <Text>{index + 1}. {item.content}</Text>
      </Space>
    </List.Item>
  );
};

const TalkBoost: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState<'userId' | 'talkId'>('talkId');
  const [searchValue, setSearchValue] = useState('');
  const [talks, setTalks] = useState<TalkInfo[]>([]);
  const [selectedTalks, setSelectedTalks] = useState<number[]>([]);
  const [taskItems, setTaskItems] = useState<Map<number, TaskItem>>(new Map());
  const [distributeDays, setDistributeDays] = useState(8);
  const [editingTalkId, setEditingTalkId] = useState<number | null>(null);
  const [editingComments, setEditingComments] = useState<CommentItem[]>([]);
  const [newComment, setNewComment] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiType, setAiType] = useState<'deepseek' | 'claude'>('deepseek');
  const [aiGenerateCount, setAiGenerateCount] = useState<number>(3);
  const [submitting, setSubmitting] = useState(false);

  // Tab 相关状态
  const [activeTab, setActiveTab] = useState('list');
  const [taskList, setTaskList] = useState<TaskStatus[]>([]);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskStatus | null>(null);
  const [taskSteps, setTaskSteps] = useState<TaskStep[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // 步骤评论编辑状态
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [editingStepComment, setEditingStepComment] = useState('');
  const [stepUpdateLoading, setStepUpdateLoading] = useState(false);

  // 评论审查状态
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewTask, setReviewTask] = useState<TaskStatus | null>(null);
  const [reviewSteps, setReviewSteps] = useState<TaskStep[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [cancellingStepId, setCancellingStepId] = useState<number | null>(null);
  const [sendingStepId, setSendingStepId] = useState<number | null>(null);
  const [deletingStepId, setDeletingStepId] = useState<number | null>(null);

  // 新增评论状态：支持多行批量添加
  interface PendingNewComment {
    id: string;
    content: string;
    time: dayjs.Dayjs | null;
  }
  const [pendingNewComments, setPendingNewComments] = useState<PendingNewComment[]>([]);
  const [addAllLoading, setAddAllLoading] = useState(false);

  // 任务取消状态
  const [cancelTaskModalVisible, setCancelTaskModalVisible] = useState(false);
  const [cancellingTask, setCancellingTask] = useState<TaskStatus | null>(null);
  const [cancelTaskLoading, setCancelTaskLoading] = useState(false);

  // 批量选择状态
  const [batchSelectMode, setBatchSelectMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [batchCancelLoading, setBatchCancelLoading] = useState(false);

  // 任务列表筛选/排序
  type TaskSortKey =
    | 'createTimeDesc'
    | 'createTimeAsc'
    | 'likeCompletedDesc'
    | 'commentCompletedDesc';
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterKeyword, setFilterKeyword] = useState<string>('');
  // 防抖后真正用于请求的关键词
  const [appliedKeyword, setAppliedKeyword] = useState<string>('');
  const [filterCreateRange, setFilterCreateRange] =
    useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [taskSortKey, setTaskSortKey] = useState<TaskSortKey>('createTimeDesc');

  // 服务端分页
  const [taskListPage, setTaskListPage] = useState<number>(1);
  const [taskListPageSize, setTaskListPageSize] = useState<number>(10);
  const [taskListTotal, setTaskListTotal] = useState<number>(0);

  // 关键词输入防抖（350ms），同时把页码回到 1
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedKeyword(filterKeyword.trim());
      setTaskListPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [filterKeyword]);

  // 筛选/排序变更时回到首页（关键词由上面的 effect 处理）
  const handleFilterStatusChange = (v: string[]) => {
    setTaskListPage(1);
    setFilterStatus(v);
  };
  const handleFilterRangeChange = (v: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setTaskListPage(1);
    setFilterCreateRange(v);
  };
  const handleSortKeyChange = (v: TaskSortKey) => {
    setTaskListPage(1);
    setTaskSortKey(v);
  };

  const resetTaskFilters = () => {
    setFilterStatus([]);
    setFilterKeyword('');
    setAppliedKeyword('');
    setFilterCreateRange(null);
    setTaskSortKey('createTimeDesc');
    setTaskListPage(1);
  };

  // 专属机器人员工列表
  const [botEmployees, setBotEmployees] = useState<BotEmployee[]>([]);
  const [changingBotStepId, setChangingBotStepId] = useState<number | null>(null);

  // 自动增长状态
  const [autoBoostStatus, setAutoBoostStatus] = useState<AutoBoostStatus | null>(null);
  const [autoBoostLoading, setAutoBoostLoading] = useState(false);
  const autoBoostTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 拖拽排序
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleEditingDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setEditingComments((items) => {
        const oldIndex = items.findIndex((_, i) => `editing-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `editing-${i}` === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // 获取任务列表（服务端筛选/排序/分页）
  const fetchTaskList = useCallback(async () => {
    setTaskListLoading(true);
    try {
      const [from, to] = filterCreateRange || [null, null];
      const params: Record<string, unknown> = {
        sort: taskSortKey,
        page: taskListPage,
        size: taskListPageSize,
      };
      if (filterStatus.length > 0) params.statuses = filterStatus;
      if (appliedKeyword) params.keyword = appliedKeyword;
      if (from) params.createTimeFrom = from.format('YYYY-MM-DD');
      if (to) params.createTimeTo = to.format('YYYY-MM-DD');

      const response = await apiPost('/api/talk-boost/tasks', params);
      if (response.code === 0 && response.data) {
        const data = response.data as { records?: TaskStatus[]; total?: number };
        setTaskList(data.records || []);
        setTaskListTotal(data.total || 0);
      } else {
        message.error(response.msg || '获取任务列表失败');
      }
    } catch (error) {
      message.error('获取任务列表失败');
    } finally {
      setTaskListLoading(false);
    }
  }, [filterStatus, appliedKeyword, filterCreateRange, taskSortKey, taskListPage, taskListPageSize]);

  // 获取任务详情（步骤列表）
  const fetchTaskSteps = useCallback(async (taskId: string) => {
    setStepsLoading(true);
    try {
      const response = await apiPost('/api/talk-boost/task-steps', { taskId });
      if (response.code === 0 && response.data) {
        setTaskSteps(response.data);
      } else {
        message.error(response.msg || '获取步骤列表失败');
      }
    } catch (error) {
      message.error('获取步骤列表失败');
    } finally {
      setStepsLoading(false);
    }
  }, []);

  // 查看任务详情
  const viewTaskDetail = (task: TaskStatus) => {
    setSelectedTask(task);
    setDetailModalVisible(true);
    fetchTaskSteps(task.taskId);
  };

  // 更新步骤评论内容
  const handleUpdateStepComment = useCallback(async () => {
    if (editingStepId === null || !editingStepComment.trim()) {
      message.warning('评论内容不能为空');
      return;
    }
    setStepUpdateLoading(true);
    try {
      const response = await apiPost('/api/talk-boost/task-step/update-comment', {
        stepId: editingStepId,
        commentContent: editingStepComment.trim(),
      });
      if (response.code === 0) {
        message.success('评论内容已更新');
        setEditingStepId(null);
        setEditingStepComment('');
        // 刷新步骤列表
        if (selectedTask) {
          fetchTaskSteps(selectedTask.taskId);
        }
      } else {
        message.error(response.msg || '更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setStepUpdateLoading(false);
    }
  }, [editingStepId, editingStepComment, selectedTask, fetchTaskSteps]);

  // 打开评论审查面板
  const openCommentReview = useCallback(async (task: TaskStatus) => {
    setReviewTask(task);
    setReviewModalVisible(true);
    setReviewLoading(true);
    try {
      const response = await apiPost('/api/talk-boost/task-steps', { taskId: task.taskId });
      if (response.code === 0 && response.data) {
        // 只展示评论类型的步骤
        const commentSteps = (response.data as TaskStep[]).filter(s => s.stepType === 'comment');
        setReviewSteps(commentSteps);
      } else {
        message.error(response.msg || '获取评论列表失败');
      }
    } catch (error) {
      message.error('获取评论列表失败');
    } finally {
      setReviewLoading(false);
    }
  }, []);

  // 刷新评论审查列表
  const refreshReviewSteps = useCallback(async () => {
    if (!reviewTask) return;
    setReviewLoading(true);
    try {
      const response = await apiPost('/api/talk-boost/task-steps', { taskId: reviewTask.taskId });
      if (response.code === 0 && response.data) {
        const commentSteps = (response.data as TaskStep[]).filter(s => s.stepType === 'comment');
        setReviewSteps(commentSteps);
      }
    } catch (error) {
      // silent
    } finally {
      setReviewLoading(false);
    }
  }, [reviewTask]);

  // 取消评论步骤
  const handleCancelStep = useCallback(async (stepId: number) => {
    setCancellingStepId(stepId);
    try {
      const response = await apiPost('/api/talk-boost/task-step/cancel', { stepId });
      if (response.code === 0) {
        message.success('已取消发送');
        refreshReviewSteps();
        // 同步刷新任务列表
        fetchTaskList();
      } else {
        message.error(response.msg || '取消失败');
      }
    } catch (error) {
      message.error('取消失败');
    } finally {
      setCancellingStepId(null);
    }
  }, [refreshReviewSteps, fetchTaskList]);

  // 立即发送评论步骤
  const handleSendStepNow = useCallback(async (stepId: number) => {
    setSendingStepId(stepId);
    try {
      const response = await apiPost('/api/talk-boost/task-step/send-now', { stepId });
      if (response.code === 0) {
        message.success('发送成功');
        refreshReviewSteps();
        fetchTaskList();
      } else {
        message.error(response.msg || '发送失败');
      }
    } catch (error) {
      message.error('发送失败');
    } finally {
      setSendingStepId(null);
    }
  }, [refreshReviewSteps, fetchTaskList]);

  // 删除已发送的评论
  const handleDeleteComment = useCallback(async (stepId: number) => {
    setDeletingStepId(stepId);
    try {
      const response = await apiPost('/api/talk-boost/task-step/delete-comment', { stepId });
      if (response.code === 0) {
        message.success('评论已删除');
        refreshReviewSteps();
        fetchTaskList();
      } else {
        message.error(response.msg || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    } finally {
      setDeletingStepId(null);
    }
  }, [refreshReviewSteps, fetchTaskList]);

  // 多行评论：增/改/删 + 全部添加
  const newPendingId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID() : `pc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const addPendingCommentRow = useCallback(() => {
    setPendingNewComments(prev => [...prev, { id: newPendingId(), content: '', time: null }]);
  }, []);

  const updatePendingComment = useCallback((id: string, patch: Partial<PendingNewComment>) => {
    setPendingNewComments(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  }, []);

  const removePendingComment = useCallback((id: string) => {
    setPendingNewComments(prev => prev.filter(c => c.id !== id));
  }, []);

  const handleAddAllComments = useCallback(async () => {
    if (!reviewTask) return;
    const valid = pendingNewComments.filter(c => c.content.trim());
    if (valid.length === 0) {
      message.warning('请先添加并填写评论内容');
      return;
    }
    setAddAllLoading(true);
    let ok = 0;
    const failedIds = new Set<string>();
    for (const c of valid) {
      try {
        const scheduledTime = (c.time || dayjs().add(5, 'minute')).format('YYYY-MM-DD HH:mm:ss');
        const response = await apiPost('/api/talk-boost/task-step/add-comment', {
          taskId: reviewTask.taskId,
          commentContent: c.content.trim(),
          scheduledTime,
        });
        if (response.code === 0) ok++;
        else failedIds.add(c.id);
      } catch {
        failedIds.add(c.id);
      }
    }
    setAddAllLoading(false);
    refreshReviewSteps();
    fetchTaskList();
    if (failedIds.size === 0) {
      message.success(`成功添加 ${ok} 条评论`);
      setPendingNewComments([]);
    } else {
      message.warning(`成功 ${ok} 条，失败 ${failedIds.size} 条`);
      // 只保留失败的让用户重试
      setPendingNewComments(prev => prev.filter(c => failedIds.has(c.id)));
    }
  }, [reviewTask, pendingNewComments, refreshReviewSteps, fetchTaskList]);

  // 在评论审查面板中编辑后刷新
  const handleReviewUpdateComment = useCallback(async () => {
    if (editingStepId === null || !editingStepComment.trim()) {
      message.warning('评论内容不能为空');
      return;
    }
    setStepUpdateLoading(true);
    try {
      const response = await apiPost('/api/talk-boost/task-step/update-comment', {
        stepId: editingStepId,
        commentContent: editingStepComment.trim(),
      });
      if (response.code === 0) {
        message.success('评论内容已更新');
        setEditingStepId(null);
        setEditingStepComment('');
        refreshReviewSteps();
      } else {
        message.error(response.msg || '更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setStepUpdateLoading(false);
    }
  }, [editingStepId, editingStepComment, refreshReviewSteps]);

  // 取消任务
  const handleCancelTask = useCallback(async () => {
    if (!cancellingTask) return;
    setCancelTaskLoading(true);
    try {
      const response = await apiPost(`/api/talk-boost/task/${cancellingTask.taskId}/cancel`, {});
      if (response.code === 0) {
        message.success('任务已取消');
        setCancelTaskModalVisible(false);
        setCancellingTask(null);
        fetchTaskList();
      } else {
        message.error(response.msg || '取消失败');
      }
    } catch (error) {
      message.error('取消失败');
    } finally {
      setCancelTaskLoading(false);
    }
  }, [cancellingTask, fetchTaskList]);

  // 批量取消任务
  const handleBatchCancelTasks = useCallback(async () => {
    if (selectedTaskIds.length === 0) return;
    setBatchCancelLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const taskId of selectedTaskIds) {
      try {
        const response = await apiPost(`/api/talk-boost/task/${taskId}/cancel`, {});
        if (response.code === 0) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBatchCancelLoading(false);
    setCancelTaskModalVisible(false);
    setSelectedTaskIds([]);
    fetchTaskList();
    if (failCount === 0) {
      message.success(`已取消 ${successCount} 个任务`);
    } else {
      message.warning(`成功 ${successCount} 个，失败 ${failCount} 个`);
    }
  }, [selectedTaskIds, fetchTaskList]);

  // Tab 切换时加载数据
  useEffect(() => {
    if (activeTab === 'list') {
      fetchTaskList();
    }
  }, [activeTab, fetchTaskList]);

  // 获取自动增长状态
  const fetchAutoBoostStatus = useCallback(async () => {
    setAutoBoostLoading(true);
    try {
      const response = await apiGet('/api/talk-boost/auto-boost/status');
      if (response.code === 0 && response.data) {
        setAutoBoostStatus(response.data);
      }
    } catch (error) {
      // 静默失败，不弹提示
    } finally {
      setAutoBoostLoading(false);
    }
  }, []);

  // 自动增长 Tab 的定时刷新 (每分钟)
  useEffect(() => {
    if (activeTab === 'auto') {
      fetchAutoBoostStatus();
      autoBoostTimerRef.current = setInterval(fetchAutoBoostStatus, 60000);
    }
    return () => {
      if (autoBoostTimerRef.current) {
        clearInterval(autoBoostTimerRef.current);
        autoBoostTimerRef.current = null;
      }
    };
  }, [activeTab, fetchAutoBoostStatus]);

  // 加载专属机器人员工列表
  useEffect(() => {
    getEmployeeList().then(setBotEmployees).catch(() => setBotEmployees([]));
  }, []);

  // 修改步骤发送人
  const handleChangeStepBot = useCallback(async (stepId: number, botId: number) => {
    setChangingBotStepId(stepId);
    try {
      const response = await apiPost('/api/talk-boost/task-step/change-bot', { stepId, botId });
      if (response.code === 0) {
        message.success('发送人修改成功');
        refreshReviewSteps();
      } else {
        message.error(response.msg || '修改失败');
      }
    } catch (error) {
      message.error('修改失败');
    } finally {
      setChangingBotStepId(null);
    }
  }, [refreshReviewSteps]);

  // 查询帖子
  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      message.warning('请输入查询条件');
      return;
    }

    setLoading(true);
    try {
      const params: { userId?: number; talkIds?: number[] } = {};
      if (searchType === 'userId') {
        params.userId = parseInt(searchValue);
      } else {
        params.talkIds = searchValue.split(',').map(id => parseInt(id.trim()));
      }

      const response = await apiPost('/api/talk-boost/query', params);
      if (response.code === 0 && response.data) {
        const talkList = response.data.talks || [];
        setTalks(talkList);

        // 默认不勾选
        setSelectedTalks([]);

        // 初始化任务项
        const newTaskItems = new Map<number, TaskItem>();
        talkList.forEach((t: TalkInfo) => {
          newTaskItems.set(t.talkId, {
            talkId: t.talkId,
            content: t.content,
            authorName: t.authorName,
            currentLikeCount: t.currentLikeCount,
            targetLikeCount: t.suggestedLikeCount,
            comments: [],
          });
        });
        setTaskItems(newTaskItems);

        message.success(`找到 ${talkList.length} 个帖子`);
      } else {
        message.error(response.msg || '查询失败');
      }
    } catch (error) {
      message.error('查询失败');
    } finally {
      setLoading(false);
    }
  }, [searchType, searchValue]);

  // 更新目标点赞数
  const updateTargetLikes = (talkId: number, value: number) => {
    setTaskItems(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(talkId);
      if (item) {
        newMap.set(talkId, { ...item, targetLikeCount: value });
      }
      return newMap;
    });
  };

  // 打开评论编辑
  const openCommentEdit = (talkId: number) => {
    const item = taskItems.get(talkId);
    setEditingTalkId(talkId);
    setEditingComments(item?.comments || []);
    setNewComment('');
    // 预填推荐数量：与"评论"列展示的 suggested 一致
    const suggested = Math.max(1, Math.floor((item?.targetLikeCount || 35) / 35));
    setAiGenerateCount(suggested);
  };

  // 保存评论
  const saveComments = () => {
    if (editingTalkId !== null) {
      setTaskItems(prev => {
        const newMap = new Map(prev);
        const item = newMap.get(editingTalkId);
        if (item) {
          newMap.set(editingTalkId, { ...item, comments: editingComments });
        }
        return newMap;
      });
      setEditingTalkId(null);
    }
  };

  // 添加评论
  const addComment = () => {
    if (newComment.trim()) {
      setEditingComments([...editingComments, { content: newComment.trim(), parentId: null }]);
      setNewComment('');
    }
  };

  // 删除评论
  const removeComment = (index: number) => {
    setEditingComments(editingComments.filter((_, i) => i !== index));
  };

  // AI 生成评论
  const generateComments = async () => {
    if (editingTalkId === null) return;

    const count = Math.max(1, Math.min(50, Math.floor(aiGenerateCount || 0)));
    if (!count) {
      message.warning('请输入要生成的评论数量');
      return;
    }

    setAiGenerating(true);
    try {
      const response = await apiPost('/api/post-comment/generate', {
        talkId: editingTalkId,
        count,
        language: 'zh-CN',
        minLength: 3,
        maxLength: 50,
        aiType,
      });

      if (response.code === 0 && response.data?.generatedComments) {
        const newComments = response.data.generatedComments.map((c: { content: string; replyTo: number | null }) => ({
          content: c.content,
          parentId: c.replyTo,
        }));
        setEditingComments([...editingComments, ...newComments]);
        message.success(`生成了 ${newComments.length} 条评论`);
      } else {
        message.error(response.msg || 'AI 生成失败');
      }
    } catch (error) {
      message.error('AI 生成失败');
    } finally {
      setAiGenerating(false);
    }
  };

  // 提交任务
  const handleSubmit = async () => {
    if (selectedTalks.length === 0) {
      message.warning('请选择至少一个帖子');
      return;
    }

    const tasks = selectedTalks.map(talkId => {
      const item = taskItems.get(talkId);
      return {
        talkId,
        likeCount: item?.targetLikeCount || 0,
        comments: item?.comments || [],
        distributeDays,
      };
    });

    setSubmitting(true);
    try {
      const response = await apiPost('/api/talk-boost/submit', { tasks });
      if (response.code === 0) {
        message.success(`成功创建 ${tasks.length} 个任务`);
        // 清空状态
        setTalks([]);
        setSelectedTalks([]);
        setTaskItems(new Map());
        setActiveTab('list');
      } else {
        message.error({ content: response.msg || '提交失败', duration: 5 });
      }
    } catch (error: any) {
      message.error({ content: error.message || '提交失败', duration: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  // 帖子列表表格列定义
  const columns = [
    {
      title: '选择',
      dataIndex: 'talkId',
      width: 60,
      render: (talkId: number, record: TalkInfo) => (
        <Checkbox
          checked={selectedTalks.includes(talkId)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedTalks([...selectedTalks, talkId]);
            } else {
              setSelectedTalks(selectedTalks.filter(id => id !== talkId));
            }
          }}
        />
      ),
    },
    {
      title: 'ID',
      dataIndex: 'talkId',
      width: 140,
      render: (talkId: number, record: TalkInfo) => (
        <Space direction="vertical" size={0}>
          <Text copyable={{ text: String(talkId) }} style={{ fontSize: 12 }}>
            帖子: {talkId}
          </Text>
          {record.authorId && (
            <Text copyable={{ text: String(record.authorId) }} type="secondary" style={{ fontSize: 12 }}>
              用户: {record.authorId}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '帖子内容',
      dataIndex: 'content',
      ellipsis: true,
      render: (content: string, record: TalkInfo) => (
        <div>
          <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
            {content}
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>
            作者: {record.authorName} | 浏览: {record.viewCount}
          </Text>
        </div>
      ),
    },
    {
      title: '当前赞',
      dataIndex: 'currentLikeCount',
      width: 80,
      align: 'center' as const,
    },
    {
      title: '目标赞',
      dataIndex: 'talkId',
      width: 120,
      render: (talkId: number) => {
        const item = taskItems.get(talkId);
        return (
          <InputNumber
            min={0}
            max={500}
            value={item?.targetLikeCount || 0}
            onChange={(v) => updateTargetLikes(talkId, v || 0)}
            size="small"
          />
        );
      },
    },
    {
      title: '预估浏览',
      dataIndex: 'talkId',
      width: 100,
      align: 'center' as const,
      render: (talkId: number) => {
        const item = taskItems.get(talkId);
        const targetLikes = item?.targetLikeCount || 0;
        // 预估浏览 = 点赞数 × 5~7 (取中间值6)
        const estimatedViews = Math.max(10, targetLikes * 6);
        return (
          <Text type="secondary">
            <EyeOutlined style={{ marginRight: 4 }} />
            ~{estimatedViews}
          </Text>
        );
      },
    },
    {
      title: '评论',
      dataIndex: 'talkId',
      width: 120,
      render: (talkId: number) => {
        const item = taskItems.get(talkId);
        const count = item?.comments.length || 0;
        const suggested = Math.max(1, Math.floor((item?.targetLikeCount || 35) / 35));
        return (
          <Space>
            <Tag color={count >= suggested ? 'green' : 'orange'}>{count} / {suggested}</Tag>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openCommentEdit(talkId)}
            />
          </Space>
        );
      },
    },
  ];

  // 任务列表表格列定义
  const taskColumns = [
    {
      title: '帖子内容',
      dataIndex: 'talkContent',
      ellipsis: true,
      render: (content: string, record: TaskStatus) => (
        <div>
          <Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0 }}>{content}</Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>作者: {record.authorName}</Text>
        </div>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'talkId',
      width: 140,
      render: (talkId: number, record: TaskStatus) => (
        <Space direction="vertical" size={0}>
          <Text copyable={{ text: String(talkId) }} style={{ fontSize: 12 }}>
            帖子: {talkId}
          </Text>
          {record.authorUserId && (
            <Text copyable={{ text: String(record.authorUserId) }} type="secondary" style={{ fontSize: 12 }}>
              用户: {record.authorUserId}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'default', text: '待执行' },
          running: { color: 'processing', text: '执行中' },
          completed: { color: 'success', text: '已完成' },
          failed: { color: 'error', text: '失败' },
          cancelled: { color: 'warning', text: '已取消' },
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '点赞',
      dataIndex: 'completedLikeCount',
      width: 100,
      render: (_: number, record: TaskStatus) => (
        <Text>{record.completedLikeCount} / {record.targetLikeCount}</Text>
      ),
    },
    {
      title: '评论',
      dataIndex: 'completedCommentCount',
      width: 100,
      render: (_: number, record: TaskStatus) => (
        <Text>{record.completedCommentCount} / {record.targetCommentCount}</Text>
      ),
    },
    {
      title: '浏览',
      dataIndex: 'completedViewCount',
      width: 100,
      render: (_: number, record: TaskStatus) => (
        <Text>{record.completedViewCount} / {record.targetViewCount}</Text>
      ),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      width: 80,
      render: (progress: number) => <Text>{progress}%</Text>,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: (createTime: string) => {
        if (!createTime) return <Text type="secondary">-</Text>;
        const t = dayjs(createTime);
        return <Text>{t.isValid() ? t.format('YYYY-MM-DD HH:mm:ss') : createTime}</Text>;
      },
    },
    {
      title: '操作',
      width: 220,
      render: (_: unknown, record: TaskStatus) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => viewTaskDetail(record)}>
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<AuditOutlined />}
            onClick={() => openCommentReview(record)}
          >
            发送评论
          </Button>
          {(record.status === 'pending' || record.status === 'running') && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => { setCancellingTask(record); setCancelTaskModalVisible(true); }}
            >
              取消
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 步骤列表表格列定义
  const stepColumns = [
    {
      title: '类型',
      dataIndex: 'stepType',
      width: 80,
      render: (type: string) => {
        const typeMap: Record<string, { color: string; text: string }> = {
          like: { color: 'blue', text: '点赞' },
          comment: { color: 'green', text: '评论' },
          view: { color: 'purple', text: '浏览' },
        };
        const t = typeMap[type] || { color: 'default', text: type };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    {
      title: '机器人',
      dataIndex: 'botName',
      width: 120,
    },
    {
      title: '浏览/评论',
      dataIndex: 'viewCount',
      width: 100,
      render: (count: number | null, record: TaskStep) => {
        if (record.stepType === 'view') {
          return count || '-';
        }
        if (record.stepType === 'comment' && record.commentContent) {
          return (
            <Text ellipsis style={{ maxWidth: 80 }} title={record.commentContent}>
              {record.commentContent}
            </Text>
          );
        }
        return '-';
      },
    },
    {
      title: '计划时间',
      dataIndex: 'scheduledTime',
      width: 160,
    },
    {
      title: '执行时间',
      dataIndex: 'executedTime',
      width: 160,
      render: (time: string | null) => time || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'default', text: '待执行' },
          running: { color: 'processing', text: '执行中' },
          completed: { color: 'success', text: '完成' },
          failed: { color: 'error', text: '失败' },
          cancelled: { color: 'warning', text: '已取消' },
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '错误',
      dataIndex: 'errorMessage',
      ellipsis: true,
      render: (msg: string | null) => msg ? <Text type="danger">{msg}</Text> : '-',
    },
    {
      title: '操作',
      width: 80,
      render: (_: unknown, record: TaskStep) => {
        if (record.stepType === 'comment' && record.status === 'pending') {
          return (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingStepId(record.id);
                setEditingStepComment(record.commentContent || '');
              }}
            >
              编辑
            </Button>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <div style={{ padding: '0 0 24px' }}>
      <PageHeader title="帖子增长助手" icon={<RocketOutlined />} />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          // 注意：'auto' Tab（自动增长）已隐藏；保留 children 代码以便日后恢复
          {
            key: 'list',
            label: (
              <span>
                <HistoryOutlined />
                任务列表
              </span>
            ),
            children: (
              <SimpleCard
                title="历史任务"
                extra={
                  <Space>
                    <Switch
                      checkedChildren="多选"
                      unCheckedChildren="多选"
                      checked={batchSelectMode}
                      onChange={(checked) => {
                        setBatchSelectMode(checked);
                        if (!checked) setSelectedTaskIds([]);
                      }}
                    />
                    {batchSelectMode && selectedTaskIds.length > 0 && (
                      <Button
                        danger
                        onClick={() => { setCancellingTask(null); setCancelTaskModalVisible(true); }}
                      >
                        批量取消 ({selectedTaskIds.length})
                      </Button>
                    )}
                    <Button onClick={fetchTaskList} loading={taskListLoading}>
                      刷新
                    </Button>
                  </Space>
                }
              >
                {/* 筛选/排序条 */}
                <div
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    background: '#fafafa',
                    border: `1px solid ${COLORS.borderDark}`,
                    borderRadius: 6,
                  }}
                >
                  <Space wrap size={[12, 8]} style={{ width: '100%' }}>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="状态（可多选）"
                      value={filterStatus}
                      onChange={handleFilterStatusChange}
                      style={{ minWidth: 220 }}
                      options={[
                        { value: 'pending', label: '待执行' },
                        { value: 'running', label: '执行中' },
                        { value: 'completed', label: '已完成' },
                        { value: 'failed', label: '失败' },
                        { value: 'cancelled', label: '已取消' },
                      ]}
                      maxTagCount="responsive"
                    />
                    <Input
                      allowClear
                      placeholder="搜索：帖子内容/作者/帖子ID/用户ID/任务ID"
                      prefix={<SearchOutlined />}
                      value={filterKeyword}
                      onChange={(e) => setFilterKeyword(e.target.value)}
                      style={{ width: 320 }}
                    />
                    <DatePicker.RangePicker
                      value={filterCreateRange as any}
                      onChange={(v) => handleFilterRangeChange(v as any)}
                      placeholder={['创建起始', '创建截止']}
                      allowClear
                    />
                    <Space size={4}>
                      <Text type="secondary">排序</Text>
                      <Select
                        value={taskSortKey}
                        onChange={(v) => handleSortKeyChange(v as TaskSortKey)}
                        style={{ width: 180 }}
                        options={[
                          { value: 'createTimeDesc', label: '创建时间 ↓ 最新优先' },
                          { value: 'createTimeAsc', label: '创建时间 ↑ 最早优先' },
                          { value: 'likeCompletedDesc', label: '点赞完成数 ↓' },
                          { value: 'commentCompletedDesc', label: '评论完成数 ↓' },
                        ]}
                      />
                    </Space>
                    <Button onClick={resetTaskFilters}>重置筛选</Button>
                    <Text type="secondary">共 {taskListTotal} 条</Text>
                  </Space>
                </div>

                <Table
                  dataSource={taskList}
                  columns={taskColumns}
                  rowKey="taskId"
                  loading={taskListLoading}
                  pagination={{
                    current: taskListPage,
                    pageSize: taskListPageSize,
                    total: taskListTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (t) => `共 ${t} 条`,
                    onChange: (p, ps) => {
                      setTaskListPage(p);
                      setTaskListPageSize(ps);
                    },
                  }}
                  rowSelection={batchSelectMode ? {
                    selectedRowKeys: selectedTaskIds,
                    onChange: (keys) => setSelectedTaskIds(keys as string[]),
                    getCheckboxProps: (record: TaskStatus) => ({
                      disabled: record.status !== 'pending' && record.status !== 'running',
                    }),
                  } : undefined}
                />
              </SimpleCard>
            ),
          },
          {
            key: 'create',
            label: (
              <span>
                <RocketOutlined />
                创建任务
              </span>
            ),
            children: (
              <>
                {/* 查询区域 */}
                <SimpleCard title="Step 1: 查询帖子">
                  <Row gutter={16} align="middle">
                    <Col>
                      <Select
                        value={searchType}
                        onChange={setSearchType}
                        style={{ width: 120 }}
                        options={[
                          { value: 'userId', label: '用户ID' },
                          { value: 'talkId', label: '帖子ID' },
                        ]}
                      />
                    </Col>
                    <Col flex="auto">
                      <Input
                        placeholder={searchType === 'userId' ? '输入用户ID' : '输入帖子ID，多个用逗号分隔'}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onPressEnter={handleSearch}
                      />
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                        loading={loading}
                      >
                        查询
                      </Button>
                    </Col>
                  </Row>
                </SimpleCard>

                {/* 帖子列表 */}
                {talks.length > 0 && (
                  <SimpleCard
                    title="Step 2: 选择帖子并设置"
                    extra={
                      <Text type="secondary">
                        已选择 {selectedTalks.length} / {talks.length} 个帖子
                      </Text>
                    }
                    style={{ marginTop: 16 }}
                  >
                    <Table
                      dataSource={talks}
                      columns={columns}
                      rowKey="talkId"
                      pagination={false}
                      size="middle"
                    />
                  </SimpleCard>
                )}

                {/* 提交区域 */}
                {selectedTalks.length > 0 && (
                  <SimpleCard title="Step 3: 确认提交" style={{ marginTop: 16 }}>
                    <Row gutter={24} align="middle">
                      <Col>
                        <Text>分布天数:</Text>
                      </Col>
                      <Col span={6}>
                        <Slider
                          min={1}
                          max={14}
                          value={distributeDays}
                          onChange={setDistributeDays}
                          marks={{
                            1: '1天',
                            3: '3天',
                            7: '7天',
                            10: '10天',
                            14: '14天'
                          }}
                        />
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Space>
                          <Text type="secondary">
                            总点赞: {Array.from(taskItems.values())
                              .filter(item => selectedTalks.includes(item.talkId))
                              .reduce((sum, item) => sum + item.targetLikeCount, 0)}
                          </Text>
                          <Text type="secondary">
                            总评论: {Array.from(taskItems.values())
                              .filter(item => selectedTalks.includes(item.talkId))
                              .reduce((sum, item) => sum + item.comments.length, 0)}
                          </Text>
                          <Button
                            type="primary"
                            size="large"
                            icon={<RocketOutlined />}
                            onClick={handleSubmit}
                            loading={submitting}
                          >
                            提交任务
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </SimpleCard>
                )}
              </>
            ),
          },
          ...([] as any[]), // 自动增长 Tab 已隐藏；如需恢复，把下面这条 false && 改回去
          ...((false as boolean) ? [{
            key: 'auto',
            label: (
              <span>
                <ThunderboltOutlined />
                自动增长
              </span>
            ),
            children: (
              <SimpleCard
                title="每日自动增长任务"
                extra={
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      每分钟自动刷新
                    </Text>
                    <Button
                      icon={<SyncOutlined spin={autoBoostLoading} />}
                      onClick={fetchAutoBoostStatus}
                      loading={autoBoostLoading}
                    >
                      刷新
                    </Button>
                  </Space>
                }
              >
                {autoBoostStatus ? (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                      <Col span={6}>
                        <Card size="small">
                          <Statistic
                            title="定时任务"
                            valueRender={() => (
                              <Badge
                                status={autoBoostStatus.enabled ? 'success' : 'default'}
                                text={autoBoostStatus.enabled ? '已启用' : '未启用'}
                              />
                            )}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small">
                          <Statistic
                            title="运行状态"
                            valueRender={() => (
                              <Badge
                                status={autoBoostStatus.running ? 'processing' : 'default'}
                                text={autoBoostStatus.running ? '执行中' : '空闲'}
                              />
                            )}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small">
                          <Statistic
                            title="AI 类型"
                            value={autoBoostStatus.aiType || '-'}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small">
                          <Statistic
                            title="评论语言"
                            value={autoBoostStatus.commentLanguage || '-'}
                          />
                        </Card>
                      </Col>
                    </Row>

                    <Descriptions
                      title="上次运行结果"
                      bordered
                      size="small"
                      column={2}
                    >
                      <Descriptions.Item label="运行时间" span={2}>
                        {autoBoostStatus.lastRunTime || '尚未运行'}
                      </Descriptions.Item>
                      <Descriptions.Item label="处理帖子数">
                        <Text strong>{autoBoostStatus.lastProcessedCount}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="提交任务数">
                        <Text strong style={{ color: COLORS.success }}>
                          {autoBoostStatus.lastSubmittedCount}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="跳过帖子数">
                        <Text type="secondary">{autoBoostStatus.lastSkippedCount}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="失败数">
                        <Text
                          style={autoBoostStatus.lastFailedCount > 0 ? { color: COLORS.error } : undefined}
                          type={autoBoostStatus.lastFailedCount > 0 ? undefined : 'secondary'}
                        >
                          {autoBoostStatus.lastFailedCount}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>

                    <div style={{ marginTop: 24, padding: '12px 16px', background: '#f6f8fa', borderRadius: 6 }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        说明：定时任务默认每天凌晨 1:00 执行，自动遍历昨天所有新帖，使用 AI 生成评论并提交增长任务。
                        通过环境变量 <Text code>TALK_BOOST_AUTO_ENABLED=true</Text> 启用。
                      </Text>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                    {autoBoostLoading ? <LoadingOutlined style={{ fontSize: 24 }} /> : '暂无数据'}
                  </div>
                )}
              </SimpleCard>
            ),
          }] : []),
        ]}
      />

      {/* 任务详情弹窗 */}
      <Modal
        title={`任务详情 - ${selectedTask?.taskId?.slice(0, 8)}...`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedTask && (
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="点赞进度"
                  value={selectedTask.completedLikeCount}
                  suffix={`/ ${selectedTask.targetLikeCount}`}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="评论进度"
                  value={selectedTask.completedCommentCount}
                  suffix={`/ ${selectedTask.targetCommentCount}`}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="浏览进度"
                  value={selectedTask.completedViewCount}
                  suffix={`/ ${selectedTask.targetViewCount}`}
                />
              </Col>
            </Row>
          </div>
        )}
        <Table
          dataSource={taskSteps}
          columns={stepColumns}
          rowKey="id"
          loading={stepsLoading}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Modal>

      {/* 步骤评论编辑弹窗 */}
      <Modal
        title="编辑评论内容"
        open={editingStepId !== null && !reviewModalVisible}
        onOk={handleUpdateStepComment}
        onCancel={() => { setEditingStepId(null); setEditingStepComment(''); }}
        confirmLoading={stepUpdateLoading}
        okText="保存"
        cancelText="取消"
      >
        <TextArea
          rows={4}
          value={editingStepComment}
          onChange={(e) => setEditingStepComment(e.target.value)}
          placeholder="输入新的评论内容"
        />
      </Modal>

      {/* 发送评论弹窗 */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            发送评论
            {reviewTask && (
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>
                - 帖子 {reviewTask.talkId}
              </Text>
            )}
          </Space>
        }
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          setReviewTask(null);
          setReviewSteps([]);
          setPendingNewComments([]);
        }}
        footer={null}
        width={800}
      >
        {reviewTask && (
          <>
            {/* 帖子内容区域 */}
            <div style={{
              padding: 16,
              background: '#f6f8fa',
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e8e8e8',
            }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 13, color: '#666' }}>帖子内容</Text>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                  作者: {reviewTask.authorName}
                </Text>
              </div>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {reviewTask.talkContent}
              </Paragraph>

              {/* 帖子媒体内容（图片/视频） */}
              {(() => {
                if (!reviewTask.talkSnapshot) return null;
                try {
                  const snapshot = JSON.parse(reviewTask.talkSnapshot);
                  const videoUrl = snapshot.videoUrl;
                  const videoCover = snapshot.videoCover;
                  const imageList: string[] = snapshot.imageList || (snapshot.imageUrl ? [snapshot.imageUrl] : []);
                  if (!videoUrl && imageList.length === 0) return null;
                  return (
                    <div style={{ marginTop: 12 }}>
                      {videoUrl && (
                        <video
                          src={videoUrl}
                          poster={videoCover || undefined}
                          controls
                          preload="metadata"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 360,
                            borderRadius: 6,
                            background: '#000',
                          }}
                        />
                      )}
                      {!videoUrl && imageList.length > 0 && (
                        <Image.PreviewGroup>
                          <Space wrap size={8}>
                            {imageList.map((url: string, i: number) => (
                              <Image
                                key={i}
                                src={url}
                                alt={`图片${i + 1}`}
                                style={{
                                  maxHeight: imageList.length === 1 ? 300 : 160,
                                  borderRadius: 6,
                                  objectFit: 'contain',
                                }}
                                placeholder={
                                  <div style={{ padding: 20, textAlign: 'center' }}>
                                    <PictureOutlined style={{ fontSize: 24, color: '#ccc' }} />
                                  </div>
                                }
                              />
                            ))}
                          </Space>
                        </Image.PreviewGroup>
                      )}
                    </div>
                  );
                } catch {
                  return null;
                }
              })()}
            </div>

            {/* 评论列表 */}
            <div style={{ marginBottom: 12 }}>
              <Space>
                <Text strong>评论列表</Text>
                <Tag>{reviewSteps.length} 条</Tag>
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={refreshReviewSteps}
                  loading={reviewLoading}
                >
                  刷新
                </Button>
              </Space>
            </div>

            {reviewLoading && reviewSteps.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <LoadingOutlined style={{ fontSize: 24 }} />
              </div>
            ) : (
              <List
                dataSource={reviewSteps}
                locale={{ emptyText: '暂无评论步骤' }}
                renderItem={(step, index) => {
                  const isPending = step.status === 'pending';
                  const isCancelled = step.status === 'cancelled';
                  const isCompleted = step.status === 'completed';
                  const isFailed = step.status === 'failed';

                  const statusTag = isCancelled
                    ? <Tag color="warning">已取消</Tag>
                    : isCompleted
                    ? <Tag color="success">已发送</Tag>
                    : isFailed
                    ? <Tag color="error">失败</Tag>
                    : step.status === 'running'
                    ? <Tag color="processing">执行中</Tag>
                    : <Tag color="default">待发送</Tag>;

                  return (
                    <List.Item
                      style={{
                        opacity: isCancelled ? 0.5 : 1,
                        background: isPending ? '#fff' : '#fafafa',
                        padding: '12px 16px',
                        borderRadius: 6,
                        marginBottom: 8,
                        border: `1px solid ${isPending ? '#d9d9d9' : '#f0f0f0'}`,
                      }}
                      actions={isPending ? [
                        <Button
                          key="send-now"
                          type="link"
                          size="small"
                          icon={<ThunderboltOutlined />}
                          loading={sendingStepId === step.id}
                          onClick={() => handleSendStepNow(step.id)}
                        >
                          立即发送
                        </Button>,
                        <Button
                          key="edit"
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingStepId(step.id);
                            setEditingStepComment(step.commentContent || '');
                          }}
                        >
                          修改
                        </Button>,
                        <Button
                          key="cancel"
                          type="link"
                          size="small"
                          danger
                          icon={<CloseCircleOutlined />}
                          loading={cancellingStepId === step.id}
                          onClick={() => handleCancelStep(step.id)}
                        >
                          取消发送
                        </Button>,
                      ] : isFailed ? [
                        <Button
                          key="resend"
                          type="link"
                          size="small"
                          icon={<ThunderboltOutlined />}
                          loading={sendingStepId === step.id}
                          onClick={() => handleSendStepNow(step.id)}
                        >
                          重新发送
                        </Button>,
                      ] : isCompleted ? [
                        <Popconfirm
                          key="delete"
                          title="确认删除该评论？"
                          description="评论将从平台上永久删除，不可恢复"
                          onConfirm={() => handleDeleteComment(step.id)}
                          okText="删除"
                          cancelText="取消"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingStepId === step.id}
                          >
                            删除评论
                          </Button>
                        </Popconfirm>,
                      ] : []}
                    >
                      <List.Item.Meta
                        title={
                          <Space size={8}>
                            <Text type="secondary" style={{ fontSize: 12 }}>#{index + 1}</Text>
                            {statusTag}
                            {isPending && botEmployees.length > 0 ? (
                              <Select
                                value={step.botId}
                                onChange={(v) => handleChangeStepBot(step.id, v)}
                                loading={changingBotStepId === step.id}
                                size="small"
                                style={{ width: 140, fontSize: 12 }}
                                options={[
                                  // 当前机器人不在员工列表中时，作为首选项显示
                                  ...(step.botId && !botEmployees.some(b => b.botId === step.botId)
                                    ? [{ value: step.botId, label: step.botName || `Bot#${step.botId}` }]
                                    : []),
                                  ...botEmployees.map(b => ({
                                    value: b.botId,
                                    label: b.userName || `Bot#${b.botId}`,
                                  })),
                                ]}
                              />
                            ) : (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {step.botName}
                              </Text>
                            )}
                            {step.scheduledTime && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {isPending ? '计划' : ''}
                                {step.executedTime || step.scheduledTime}
                              </Text>
                            )}
                          </Space>
                        }
                        description={
                          <Text
                            style={{
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              color: isCancelled ? '#999' : undefined,
                            }}
                          >
                            {step.commentContent}
                          </Text>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}

            {/* 新增评论：可同时配置多条，一键全部添加 */}
            <div style={{
              marginTop: 16,
              padding: 16,
              background: '#f6f8fa',
              borderRadius: 8,
              border: '1px solid #e8e8e8',
            }}>
              <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
                <Col>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={addPendingCommentRow}
                  >
                    新增评论
                  </Button>
                  {pendingNewComments.length > 0 && (
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      待添加 {pendingNewComments.length} 条
                    </Text>
                  )}
                </Col>
              </Row>

              {pendingNewComments.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  点击"新增评论"开始添加；可一次性配置多条，最后点"全部添加"统一提交
                </Text>
              ) : (
                <>
                  {pendingNewComments.map((c, idx) => (
                    <div
                      key={c.id}
                      style={{
                        background: '#fff',
                        border: '1px solid #e8e8e8',
                        borderRadius: 6,
                        padding: 10,
                        marginBottom: 8,
                      }}
                    >
                      <Row align="middle" gutter={8} style={{ marginBottom: 6 }}>
                        <Col>
                          <Text strong style={{ fontSize: 12 }}>#{idx + 1}</Text>
                        </Col>
                        <Col flex="auto" />
                        <Col>
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removePendingComment(c.id)}
                          />
                        </Col>
                      </Row>
                      <TextArea
                        rows={2}
                        value={c.content}
                        onChange={(e) => updatePendingComment(c.id, { content: e.target.value })}
                        placeholder="输入评论内容"
                        style={{ marginBottom: 6 }}
                      />
                      <Row gutter={8} align="middle">
                        <Col>
                          <Text type="secondary" style={{ fontSize: 12 }}>发送时间：</Text>
                        </Col>
                        <Col>
                          <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            value={c.time || dayjs().add(5, 'minute')}
                            onChange={(val) => updatePendingComment(c.id, { time: val })}
                            size="small"
                            style={{ width: 200 }}
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <Row justify="end" style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddAllComments}
                      loading={addAllLoading}
                      disabled={!pendingNewComments.some(c => c.content.trim())}
                    >
                      全部添加 ({pendingNewComments.filter(c => c.content.trim()).length})
                    </Button>
                  </Row>
                </>
              )}
            </div>

            {/* 审查中编辑评论弹窗 */}
            <Modal
              title="修改评论内容"
              open={editingStepId !== null && reviewModalVisible}
              onOk={handleReviewUpdateComment}
              onCancel={() => { setEditingStepId(null); setEditingStepComment(''); }}
              confirmLoading={stepUpdateLoading}
              okText="保存"
              cancelText="取消"
            >
              <TextArea
                rows={4}
                value={editingStepComment}
                onChange={(e) => setEditingStepComment(e.target.value)}
                placeholder="输入新的评论内容"
              />
            </Modal>
          </>
        )}
      </Modal>

      {/* 任务取消确认弹窗（单个 + 批量） */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: COLORS.warning }} />
            {cancellingTask ? '确认取消任务' : `确认批量取消 ${selectedTaskIds.length} 个任务`}
          </Space>
        }
        open={cancelTaskModalVisible}
        onOk={cancellingTask ? handleCancelTask : handleBatchCancelTasks}
        onCancel={() => { if (!cancelTaskLoading && !batchCancelLoading) { setCancelTaskModalVisible(false); setCancellingTask(null); } }}
        confirmLoading={cancelTaskLoading || batchCancelLoading}
        okText="确认取消"
        cancelText="返回"
        okButtonProps={{ danger: true }}
        closable={!cancelTaskLoading && !batchCancelLoading}
        maskClosable={!cancelTaskLoading && !batchCancelLoading}
      >
        {cancellingTask ? (
          <div>
            <Paragraph>确定要取消以下任务吗？取消后所有待执行的步骤将不再执行。</Paragraph>
            <div style={{ padding: '12px 16px', background: '#f6f8fa', borderRadius: 6 }}>
              <Text type="secondary">帖子 ID：</Text>
              <Text strong>{cancellingTask.talkId}</Text>
              <br />
              <Text type="secondary">帖子内容：</Text>
              <Paragraph ellipsis={{ rows: 2 }} style={{ margin: '4px 0 0' }}>
                {cancellingTask.talkContent}
              </Paragraph>
            </div>
          </div>
        ) : (
          <div>
            <Paragraph>
              确定要取消选中的 <Text strong>{selectedTaskIds.length}</Text> 个任务吗？取消后所有待执行的步骤将不再执行。
            </Paragraph>
            <div style={{ padding: '12px 16px', background: '#f6f8fa', borderRadius: 6, maxHeight: 200, overflow: 'auto' }}>
              {taskList
                .filter(t => selectedTaskIds.includes(t.taskId))
                .map(t => (
                  <div key={t.taskId} style={{ marginBottom: 8 }}>
                    <Text type="secondary">帖子 {t.talkId}：</Text>
                    <Text ellipsis style={{ maxWidth: 400 }}>{t.talkContent}</Text>
                  </div>
                ))
              }
            </div>
          </div>
        )}
      </Modal>

      {/* 评论编辑弹窗 */}
      <Modal
        title="编辑评论"
        open={editingTalkId !== null}
        onOk={saveComments}
        onCancel={() => setEditingTalkId(null)}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <InputNumber
              min={1}
              max={50}
              value={aiGenerateCount}
              onChange={(v) => setAiGenerateCount(v || 1)}
              addonBefore="评论数"
              style={{ width: 140 }}
            />
            <Select
              value={aiType}
              onChange={setAiType}
              style={{ width: 100 }}
              options={[
                { value: 'deepseek', label: 'DeepSeek' },
                { value: 'claude', label: 'Claude' },
              ]}
            />
            <Button
              icon={<RobotOutlined />}
              onClick={generateComments}
              loading={aiGenerating}
            >
              AI 生成
            </Button>
          </Space>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEditingDragEnd}>
          <SortableContext items={editingComments.map((_, i) => `editing-${i}`)} strategy={verticalListSortingStrategy}>
            <List
              dataSource={editingComments}
              renderItem={(item, index) => (
                <SortableEditingCommentItem
                  key={`editing-${index}`}
                  id={`editing-${index}`}
                  item={item}
                  index={index}
                  onDelete={() => removeComment(index)}
                />
              )}
              locale={{ emptyText: '暂无评论' }}
            />
          </SortableContext>
        </DndContext>

        <div style={{ marginTop: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              placeholder="输入评论内容"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addComment}
            >
              添加
            </Button>
          </Space.Compact>
        </div>
      </Modal>
    </div>
  );
};

export default TalkBoost;
